import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { CheckCircle, Clock, DollarSign, TrendingUp, ShieldCheck, Activity, X } from 'lucide-react';
import './Grabbing.css';
import { db } from '../firebase'; 
import { ref, onValue, update } from "firebase/database"; 
import Loader from '../components/Loader'; 
import { generateRandomTask, processTaskLogic } from './GrabbingOrder';
import { useTranslation } from 'react-i18next'; // i18n hook import kiya

const Grabbing = () => {
  const { t } = useTranslation(); // Translation function initialize kiya
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [grabbing, setGrabbing] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (!savedUser) return;
    const initialUser = JSON.parse(savedUser);
    
    const userRef = ref(db, 'users/' + initialUser.username);
    const unsubscribe = onValue(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const userData = snapshot.val();
        setUser(userData);

        // --- DYNAMIC CASH GAP LOGIC ---
        if (userData.pendingTaskActive && userData.tasks) {
          const pendingTask = Object.values(userData.tasks).find(t => t.status === "pending");
          if (pendingTask) {
            const currentBalance = parseFloat(userData.balance || 0);
            const requiredAmount = parseFloat(pendingTask.orderAmount);
            
            if (currentBalance < requiredAmount) {
              const newGap = (requiredAmount - currentBalance).toFixed(2);
              if (userData.cashGap !== `-${newGap}`) {
                update(ref(db, `users/${userData.username}`), { cashGap: `-${newGap}` });
              }
            } else {
              if (userData.cashGap !== 0 && userData.cashGap !== "0") {
                update(ref(db, `users/${userData.username}`), { cashGap: 0 });
              }
            }
          }
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleGrabOrder = async () => {
    if (user?.taskActive === false) {
       Swal.fire({
         title: t('system_notification'),
         text: t('activate_tasks_msg'),
         icon: 'info',
         confirmButtonColor: '#001e50'
       });
       return;
    }

    if (user?.status === 'Freeze' || user?.status === 'Inactive') {
      Swal.fire({
        title: t('account_restricted'),
        text: t('account_freeze_msg'),
        icon: 'error',
        confirmButtonColor: '#ff0000'
      });
      return;
    }

    if (user?.pendingTaskActive) {
      Swal.fire({
        text: t('complete_pending_msg'),
        icon: 'warning',
        confirmButtonColor: '#ffb400'
      });
      return;
    }

    if (user?.completedTasks >= 25) {
      Swal.fire(t('limit_reached'), t('contact_cs_more_tasks'), 'info');
      return;
    }

    setGrabbing(true); 
    const task = await generateRandomTask(user);
    setCurrentTask(task);

    await processTaskLogic(user, task, false); 

    setTimeout(() => {
      setGrabbing(false); 
      setShowPopup(true);
    }, 2000);
  };

  const completeTask = async () => {
    try {
      await processTaskLogic(user, currentTask, true);
      setShowPopup(false);
      Swal.fire({ icon: 'success', title: t('task_completed'), timer: 1500, showConfirmButton: false });
    } catch (error) {
      // Backend se custom standard error code check kar rahe hain
      if (error.message === "Insufficient Balance" || error.message === "insufficient_balance") {
        const gapNeeded = (parseFloat(currentTask.orderAmount) - parseFloat(user.balance)).toFixed(2);
        
        Swal.fire({
          title: `<span style="font-size: 18px">${t('insufficient_funds')}</span>`,
          html: `
            <div style="font-size: 14px; color: #555; margin-bottom: 10px;">
              ${t('insufficient_balance_desc')}
            </div>
            <div style="font-weight: 700; color: #ff4d4d; font-size: 16px;">
              ${t('please_deposit')} ${gapNeeded} USDT ${t('to_continue')}.
            </div>
          `,
          icon: 'warning',
          width: '300px',
          confirmButtonText: t('ok'),
          confirmButtonColor: '#f3ba2f'
        });
        setShowPopup(false);
      } else {
        Swal.fire(t('error'), t('submission_failed'), 'error');
      }
    }
  };

  if (loading) return <Loader />;

  return (
    <>
      {grabbing && <Loader />}
      <div className={`grab-container ${grabbing ? 'is-blur' : ''}`}>
        <div className="grab-header-card">
          <p className="balance-label">{t('account_balance')}</p>
          <h2 className="balance-amount">{user?.balance || '0.00'} <span className="currency">USDT</span></h2>
        </div>

        <div className="stats-grid">
          <div className="stat-box">
            <Clock size={20} className="stat-icon-gold" />
            <div className="stat-info">
              <span className="stat-value">25</span>
              <span className="stat-label">{t('total_tasks')}</span>
            </div>
          </div>
          <div className="stat-box">
            <CheckCircle size={20} className="stat-icon-gold" />
            <div className="stat-info">
              <span className="stat-value">{user?.completedTasks || 0}/25</span>
              <span className="stat-label">{t('completed')}</span>
            </div>
          </div>
          <div className="stat-box">
            <DollarSign size={20} className="stat-icon-gold" />
            <div className="stat-info">
              <span className="stat-value">{user?.earnedCommission || '0.00'}</span>
              <span className="stat-label">{t('earned')}</span>
            </div>
          </div>
          <div className="stat-box">
            <Activity size={20} className="stat-icon-gold" />
            <div className="stat-info">
              <span className={`stat-value ${user?.status === 'Freeze' ? 'text-red' : 'text-gold'}`}>
                {user?.status ? t(user.status.toLowerCase()) : t('active')}
              </span>
              <span className="stat-label">{t('status')}</span>
            </div>
          </div>
          <div className="stat-box">
            <TrendingUp size={20} className="stat-icon-gold" />
            <div className="stat-info">
              <span className={`stat-value ${parseFloat(user?.cashGap) < 0 ? 'text-red' : ''}`}>
                {parseFloat(user?.cashGap) < 0 ? user?.cashGap : '0.00'}
              </span>
              <span className="stat-label">{t('cash_gap')}</span>
            </div>
          </div>
          <div className="stat-box">
            <ShieldCheck size={20} className="stat-icon-gold" />
            <div className="stat-info">
              <span className="stat-value">{user?.yesterdayCommission || '0.00'}</span>
              <span className="stat-label">{t('yesterday')}</span>
            </div>
          </div>
        </div>

        <div className="action-section">
          <button className="grab-main-btn" onClick={handleGrabOrder} disabled={grabbing}>
            {grabbing ? t('matching_order') : t('grab_order_btn')}
          </button>
        </div>

        {showPopup && currentTask && (
          <div className="task-overlay">
            <div className="task-popup">
              <div className="task-header">
                <span>{t('order_matching_success')}</span>
                <button className="popup-close-btn" onClick={() => setShowPopup(false)}><X size={20} /></button>
              </div>
              <div className="task-body">
                <div className="product-img-card">
                    <img src={currentTask.img} alt="Product" className="product-img-main" />
                </div>
                <h3 className="product-title-text">
                  {t(currentTask.title) || currentTask.title}
                </h3>
                
                <div className="task-info-white-box">
                  <div className="info-row">
                    <span className="info-label">{t('order_amount')}:</span> 
                    <span className="info-value">{currentTask.orderAmount} USDT</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">{t('commission_rate')}:</span> 
                    <span className="info-value text-gold">{currentTask.commRate}%</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">{t('commission_label')}:</span> 
                    <span className="info-value text-gold">{currentTask.commEarned} USDT</span>
                  </div>
                </div>
              </div>
              <button className="submit-task-btn-final" onClick={completeTask}>{t('complete_task_btn')}</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Grabbing;